import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import { ProtectedAdminRoute } from "./components/ProtectedAdminRoute";
import { AdminLayout } from "./components/AdminLayout";
import { PhotographerLayout } from "./components/PhotographerLayout";
import { Cart } from "./components/Cart";
import { SettingsProvider } from "./contexts/SettingsContext";
import { VersionUpdateNotification } from "./components/VersionUpdateNotification";
import { usePostHogTracking } from "./hooks/usePostHogTracking";

// Lazy-loaded routes HELLO!!
const NotFound = lazy(() => import("./pages/NotFound"));
const EventPage = lazy(() => import("./pages/EventPage").then((m) => ({ default: m.EventPage })));
const WinnersPage = lazy(() => import("./pages/WinnersPage").then((m) => ({ default: m.WinnersPage })));
const GroupGalleryPage = lazy(() => import("./pages/GroupGalleryPage").then((m) => ({ default: m.GroupGalleryPage })));
const Auth = lazy(() => import("./pages/Auth").then((m) => ({ default: m.Auth })));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard").then((m) => ({ default: m.AdminDashboard })));
const CreateEvent = lazy(() => import("./pages/CreateEvent").then((m) => ({ default: m.CreateEvent })));
const EventDetailView = lazy(() => import("./pages/EventDetailView").then((m) => ({ default: m.EventDetailView })));
const RaceDetailPage = lazy(() => import("./pages/RaceDetailPage").then((m) => ({ default: m.RaceDetailPage })));
const SiteSettings = lazy(() => import("./pages/SiteSettings").then((m) => ({ default: m.SiteSettings })));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminEvents = lazy(() => import("./pages/AdminEvents"));
const AdminCustomers = lazy(() => import("./pages/AdminCustomers"));
const AdminPhotographers = lazy(() => import("./pages/AdminPhotographers"));
const AdminPhotographerDetail = lazy(() => import("./pages/AdminPhotographerDetail"));
const AdminEmailTemplates = lazy(() => import("./pages/AdminEmailTemplates"));
const PhotographerDashboard = lazy(() => import("./pages/PhotographerDashboard"));
const PhotographerEvents = lazy(() => import("./pages/PhotographerEvents"));
const PhotographerSales = lazy(() => import("./pages/PhotographerSales"));
const PhotographerCustomers = lazy(() => import("./pages/PhotographerCustomers"));
const PhotographerReports = lazy(() => import("./pages/PhotographerReports"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("./pages/CheckoutCancel"));
const Account = lazy(() => import("./pages/Account"));
const CheckoutThankYou = lazy(() => import("./pages/CheckoutThankYou"));
const Search = lazy(() => import("./pages/Search"));
const Favourites = lazy(() => import("./pages/Favourites"));
const AdminVersions = lazy(() => import("./pages/AdminVersions"));
const AdminEmailQueue = lazy(() => import("./pages/AdminEmailQueue"));
const AdminMarketing = lazy(() => import("./pages/AdminMarketing"));
const AdminTools = lazy(() => import("./pages/AdminTools"));
const SharedCartPage = lazy(() => import("./pages/SharedCartPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const LoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="flex flex-col items-center space-y-4">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  </div>
);

const App = () => {
  // Single top-level PostHog identification — do not call usePostHogTracking() elsewhere
  usePostHogTracking();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <SettingsProvider>
          <Toaster />
          <Sonner />
          <VersionUpdateNotification />
          <BrowserRouter>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/account/*" element={<Account />} />
                <Route path="/search/q/:query" element={<Search />} />
                <Route path="/search/participant/:participantId" element={<Search />} />
                <Route path="/search/rider/:riderId" element={<Search />} />
                <Route path="/search/horse/:horseId" element={<Search />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/favourites" element={<Favourites />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/checkout/thank-you" element={<CheckoutThankYou />} />
                <Route path="/checkout/cancel" element={<CheckoutCancel />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/shared/:shareToken" element={<SharedCartPage />} />
                <Route path="/404" element={<NotFound />} />

                {/* Public event routes using Swiss Equestrian IDs */}
                <Route path="/:eventId" element={<EventPage />} />
                <Route path="/:eventId/winners" element={<WinnersPage />} />
                <Route path="/:eventId/:raceId" element={<EventPage />} />
                <Route path="/:eventId/:raceId/group/:groupId" element={<GroupGalleryPage />} />
                <Route path="/:eventId/:raceId/:participantId" element={<EventPage />} />

                {/* Admin routes - nested layout */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedAdminRoute>
                      <AdminLayout />
                    </ProtectedAdminRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="event/:eventId" element={<EventDetailView />} />
                  <Route path="race/:raceId" element={<RaceDetailPage />} />
                  <Route path="create-event" element={<CreateEvent />} />
                  <Route path="settings" element={<SiteSettings />} />
                  <Route path="email-templates" element={<AdminEmailTemplates />} />
                  <Route path="events" element={<AdminEvents />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="customers" element={<AdminCustomers />} />
                  <Route path="customers/:customerId" element={<CustomerDetail />} />
                  <Route path="photographers" element={<AdminPhotographers />} />
                  <Route path="photographers/:photographerId" element={<AdminPhotographerDetail />} />
                  <Route path="versions" element={<AdminVersions />} />
                  <Route path="email-queue" element={<AdminEmailQueue />} />
                  <Route path="marketing" element={<AdminMarketing />} />
                  <Route path="tools" element={<AdminTools />} />
                </Route>

                {/* Photographer routes */}
                <Route path="/photographers" element={<PhotographerLayout />}>
                  <Route index element={<PhotographerDashboard />} />
                  <Route path="events" element={<PhotographerEvents />} />
                  <Route path="sales" element={<PhotographerSales />} />
                  <Route path="customers" element={<PhotographerCustomers />} />
                  <Route path="reports" element={<PhotographerReports />} />
                </Route>

                {/* 404 catch-all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </SettingsProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
